import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Lock } from "lucide-react";
import { api, imgUrl, formatApiError } from "@/api";
import { FadeUp } from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-flame outline-none py-3 text-sm text-bone placeholder:text-faint transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-faint mb-1";

export default function ClientAccess() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [gallery, setGallery] = useState(null);
  const [activeIdx, setActiveIdx] = useState(null);

  const photos = (gallery?.photos || []).map((p, i) => ({
    ...p,
    title: `Frame ${String(i + 1).padStart(2, "0")}`,
    category: "personal",
    subcategory: "Private gallery",
    specs: "",
  }));
  const active = activeIdx !== null ? photos[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (activeIdx === null || photos.length === 0) return;
      if (e.key === "Escape") setActiveIdx(null);
      if (e.key === "ArrowRight") setActiveIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setActiveIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIdx, photos.length]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/gallery-access", { email, code });
      setGallery(data);
    } catch (error) {
      setErr(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  if (gallery) {
    return (
      <main className="min-h-screen" data-testid="client-gallery-page">
        <section className="px-4 sm:px-8 lg:px-16 pt-14 lg:pt-20 pb-10">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">Private gallery</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-bone" data-testid="client-gallery-heading">
              {gallery.client_name}&apos;s frames
            </h1>
            <p className="mt-4 text-sm text-ash">
              {photos.length} photo{photos.length === 1 ? "" : "s"} — open any frame to view it full size.
            </p>
          </FadeUp>
        </section>
        <section className="px-4 sm:px-8 lg:px-16 pb-24">
          {photos.length === 0 ? (
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint" data-testid="client-gallery-empty">
              No photos in this gallery yet — check back soon
            </p>
          ) : (
            <div className="columns-1 sm:columns-2 xl:columns-3 gap-5" data-testid="client-gallery-grid">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`client-photo-${p.id}`}
                  onClick={() => setActiveIdx(i)}
                  className="group relative block w-full mb-5 break-inside-avoid text-left border border-white/10 overflow-hidden bg-coal"
                >
                  <img
                    src={imgUrl(p.thumb)}
                    alt={p.title}
                    loading="lazy"
                    className="w-full transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="flex items-center justify-between gap-3 p-4">
                    <p className="font-serif text-lg text-bone">{p.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Private</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
        <AnimatePresence>
          {active && (
            <Lightbox
              photo={active}
              showBookCta={false}
              onClose={() => setActiveIdx(null)}
              onPrev={() => setActiveIdx((i) => (i - 1 + photos.length) % photos.length)}
              onNext={() => setActiveIdx((i) => (i + 1) % photos.length)}
            />
          )}
        </AnimatePresence>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4" data-testid="client-access-page">
      <FadeUp className="w-full max-w-md py-16">
        <form onSubmit={submit} className="border border-white/10 bg-coal p-8 sm:p-10" data-testid="client-access-form">
          <span className="inline-flex items-center justify-center w-11 h-11 border border-flame/60 text-flame mb-6">
            <Lock size={18} />
          </span>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">Client access</p>
          <h1 className="font-serif text-3xl text-bone mb-3">Your photos are waiting</h1>
          <p className="text-sm text-ash mb-8 leading-relaxed">
            Enter the email and access code Maddie sent you to open your private gallery.
          </p>
          <label className={labelCls} htmlFor="ca-email">Email</label>
          <input
            id="ca-email"
            data-testid="client-access-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className={`${inputCls} mb-6`}
          />
          <label className={labelCls} htmlFor="ca-code">Access code</label>
          <input
            id="ca-code"
            data-testid="client-access-code-input"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. 7FK2AQ"
            className={`${inputCls} mb-6 uppercase tracking-[0.3em]`}
          />
          {err && <p className="text-sm text-flame mb-4" data-testid="client-access-error">{err}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="client-access-submit-button"
            className="w-full bg-flame text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-flamebright transition-colors disabled:opacity-50"
          >
            {busy ? "Opening…" : "Open my gallery"}
          </button>
        </form>
      </FadeUp>
    </main>
  );
}
